import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { downloadsAPI } from '../services/api';
import { Plus, Trash2, Edit, Download as DownloadIcon, ArrowLeft, Upload, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'factsheet', label: 'Factsheet' },
  { value: 'report', label: 'Bericht' },
  { value: 'presentation', label: 'Präsentation' },
  { value: 'legal', label: 'Rechtliches' },
  { value: 'project', label: 'Projekt' }
];

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const AdminDownloads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload dialog state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    category: 'factsheet',
    isPublic: true
  });

  // Edit dialog state
  const [editingItem, setEditingItem] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category: 'factsheet',
    isPublic: true
  });

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    loadDownloads();
  }, [user, navigate]);

  const loadDownloads = async () => {
    try {
      setLoading(true);
      const res = await downloadsAPI.getAllAdmin();
      if (res?.success) setDownloads(res.downloads || []);
    } catch (e) {
      toast.error(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({ file: null, title: '', description: '', category: 'factsheet', isPublic: true });
    setUploadProgress(0);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Bitte eine Datei auswählen');
      return;
    }
    try {
      setUploading(true);
      setUploadProgress(0);
      await downloadsAPI.upload(
        {
          file: uploadForm.file,
          title: uploadForm.title || uploadForm.file.name,
          description: uploadForm.description,
          category: uploadForm.category,
          isPublic: uploadForm.isPublic
        },
        (progressEvent) => {
          if (progressEvent?.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      );
      toast.success('Datei erfolgreich hochgeladen');
      setIsUploadOpen(false);
      resetUploadForm();
      loadDownloads();
    } catch (err) {
      toast.error(err.message || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'factsheet',
      isPublic: !!item.isPublic
    });
    setIsEditOpen(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    try {
      await downloadsAPI.update(editingItem.id, editForm);
      toast.success('Aktualisiert');
      setIsEditOpen(false);
      setEditingItem(null);
      loadDownloads();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Diese Datei wirklich löschen?')) return;
    try {
      await downloadsAPI.remove(id);
      toast.success('Gelöscht');
      loadDownloads();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-[#C4A55A] hover:text-[#D4B56A] mb-2"
              data-testid="admin-downloads-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white">Downloads verwalten</h1>
            <p className="text-gray-400 mt-1">Factsheets, Berichte und Unterlagen verwalten</p>
          </div>

          <Dialog open={isUploadOpen} onOpenChange={(open) => {
            setIsUploadOpen(open);
            if (!open) resetUploadForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black"
                data-testid="admin-downloads-upload-open-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Datei hochladen
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-2xl text-white">Datei hochladen</DialogTitle>
                <DialogDescription className="text-gray-400">
                  PDF, DOCX, XLSX, PPTX (max. 50 MB)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="text-gray-300">Datei</Label>
                  <Input
                    type="file"
                    accept=".pdf,.docx,.xlsx,.pptx,.doc,.xls,.ppt"
                    onChange={(e) => setUploadForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white file:text-[#C4A55A] file:bg-transparent file:border-0"
                    required
                    data-testid="admin-downloads-file-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Titel</Label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Optional - Dateiname wird verwendet wenn leer"
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                    data-testid="admin-downloads-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Kategorie</Label>
                    <Select
                      value={uploadForm.category}
                      onValueChange={(v) => setUploadForm((p) => ({ ...p, category: v }))}
                    >
                      <SelectTrigger
                        className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                        data-testid="admin-downloads-category-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#C4A55A]/30 text-white">
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Öffentlich sichtbar</Label>
                    <div className="flex items-center h-10">
                      <Switch
                        checked={uploadForm.isPublic}
                        onCheckedChange={(v) => setUploadForm((p) => ({ ...p, isPublic: v }))}
                        data-testid="admin-downloads-public-switch"
                      />
                      <span className="ml-3 text-sm text-gray-400">
                        {uploadForm.isPublic ? 'Öffentlich' : 'Privat'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Beschreibung</Label>
                  <Textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))}
                    rows={3}
                    className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white resize-none"
                    data-testid="admin-downloads-description-input"
                  />
                </div>

                {uploading && (
                  <div className="space-y-1">
                    <div className="h-2 bg-[#0a0a0a] rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400">{uploadProgress}%</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                  data-testid="admin-downloads-upload-submit-btn"
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Hochladen...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Hochladen</>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-gray-400" data-testid="admin-downloads-loading">Lädt Downloads...</div>
        ) : downloads.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardContent className="py-12 text-center text-gray-400" data-testid="admin-downloads-empty">
              Noch keine Dateien hochgeladen.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-downloads-list">
            {downloads.map((d) => (
              <Card key={d.id} className="bg-[#1a1a1a] border-[#C4A55A]/20" data-testid={`admin-download-card-${d.id}`}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="p-3 rounded-lg bg-[#C4A55A]/10 border border-[#C4A55A]/30 shrink-0">
                      <FileText className="h-5 w-5 text-[#C4A55A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="px-2 py-0.5 text-xs rounded bg-[#C4A55A]/15 text-[#C4A55A]">
                          {CATEGORIES.find((c) => c.value === d.category)?.label || d.category}
                        </span>
                        {d.isPublic ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/15 text-emerald-400">Öffentlich</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded bg-zinc-500/20 text-zinc-300">Privat</span>
                        )}
                      </div>
                      <CardTitle className="text-white text-base leading-snug truncate" title={d.title}>
                        {d.title}
                      </CardTitle>
                      <p className="text-xs text-gray-500 truncate mt-1" title={d.filename}>
                        {d.filename} · {formatBytes(d.filesize)}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {d.description && (
                    <p className="text-sm text-gray-400 mb-4 line-clamp-3">{d.description}</p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">{d.downloadCount || 0} Downloads</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
                        onClick={() => window.open(downloadsAPI.getDownloadUrl(d.id), '_blank', 'noopener')}
                        data-testid={`admin-download-open-btn-${d.id}`}
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#C4A55A]/30 text-[#C4A55A] hover:bg-[#C4A55A]/10"
                        onClick={() => openEdit(d)}
                        data-testid={`admin-download-edit-btn-${d.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                        onClick={() => handleDelete(d.id)}
                        data-testid={`admin-download-delete-btn-${d.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="bg-[#1a1a1a] border-[#C4A55A]/30 max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-white">Datei bearbeiten</DialogTitle>
              <DialogDescription className="text-gray-400">
                Metadaten der Datei aktualisieren
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSave} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-gray-300">Titel</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                  className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white"
                  required
                  data-testid="admin-downloads-edit-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Kategorie</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(v) => setEditForm((p) => ({ ...p, category: v }))}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#C4A55A]/30 text-white">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Öffentlich sichtbar</Label>
                  <div className="flex items-center h-10">
                    <Switch
                      checked={editForm.isPublic}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, isPublic: v }))}
                      data-testid="admin-downloads-edit-public-switch"
                    />
                    <span className="ml-3 text-sm text-gray-400">
                      {editForm.isPublic ? 'Öffentlich' : 'Privat'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Beschreibung</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
                data-testid="admin-downloads-edit-submit-btn"
              >
                Speichern
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
